/// <reference path="../../../WebUI/typings/tsd.d.ts" />
import * as React from "react";

import * as Rest from "../Generated/RestClient";
import { resourceStrings } from "../../../Intercom.ReactUI/Views/Strings";
import { SecretControl } from "./SecretControl";
import { clientAppSettings } from "../Models/ClientAppSettings";
import { ErrorDict } from "./Shared/IBotEditor";
import reflectName from "../../../Intercom.ReactUI/Utils/NameReflector";
import { BotMsaEditor, BotMsaEditorProps } from "./BotMsaEditor";
import { BlockRowInputLabel } from './Shared/BlockRowInputLabel';
import { RollbackEndpointVersionName } from "../Utils/BotUtils"

interface OptionProps<T> {
    name: string,
    val: T,
    selected: boolean;
}

export interface BotVersionEditorProps extends React.Props<BotVersionEditor> {
    bot: Rest.BotService;
    inCreateMode: boolean;
    handleChange: (fieldName: string, ev: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => void;
    handleArrayIndexedObjectChange: (fieldName: string, index: number, childFieldName: string, ev: React.ChangeEvent<HTMLInputElement>) => void;
    handleCheckboxChange: (fieldName: string, ev: React.ChangeEvent<HTMLInputElement>) => void;
    v1Secret: string;
    errors: ErrorDict;
    isAppTypeFlighted: boolean;
}

export interface BotVersionEditorState {
    uniqueId: string;
    SchemaTransformationVersionList: OptionProps<string>[];
}

export class BotVersionEditor extends React.Component<BotVersionEditorProps, BotVersionEditorState> {

    static versionToClassname(version: string) {
        return 'version_' + version.replace('.', '_');
    }

    static versionToDisplayText(version: string) {
        return 'Version ' + version;
    }

    public static endpointId(index: number) {
        return "input-endpoints-" + index;
    }

    constructor(props) {
        super(props);

        this.state = {
            uniqueId: 'VersionEditor' + new Date().valueOf(),
            SchemaTransformationVersionList: []
        };
    }

    componentDidMount() {
        this.loadChannelSchemaTransformationInfo();
    }

    private versionRadio(index: number) {
        var endpoint = this.props.bot.Endpoints[index];
        var enabled = (this.props.bot.Version === endpoint.Version);

        return (
            <div className="version-group" key={'versionRadio_' + index}>
                <label className="version-label"><input type="radio" name="input-Version" value={endpoint.Version} checked={enabled} onChange={(ev) => this.props.handleChange('Version', ev)} /> {BotVersionEditor.versionToDisplayText(endpoint.Version)}</label>
                {this.versionContent(index, enabled)}
            </div>
        );
    };

    private versionContent(index: number, enabled: boolean) {
        const errorClass = " input-in-error";

        var endpoint = this.props.bot.Endpoints[index];
        var content: JSX.Element[];

        var x = {} as Rest.BotService;

        function reflectedName(value: any) {
            return reflectName(() => x);
        }

        switch (endpoint.Version) {
            case '1.0':

                content = [];

                if (!this.props.inCreateMode) {
                    content.push(
                        <div key="secret_1" className="detail-row">
                            <BlockRowInputLabel text="App secret" />
                            <SecretControl keyType='primary' secret={this.props.v1Secret} />
                        </div>
                    );
                }

                //we do not want the "input-appId" field to appear twice. Usually this is in the V3 ui below.
                //however there are some v1 bots which have appId populated through a flight, so keep their data.
                //only do this if we are not showing a v3 option on this page.
                if (this.props.bot.Endpoints.length === 1 && index === 0) {
                    content.push(<input key="hidden_1" type="hidden" id="input-MsaAppId" value={this.props.bot.MsaAppId} />);
                }

                break;

            case '3.0':
            default:
                const msaEditor = React.createElement(BotMsaEditor, _.extend({ key: "bot-msa-editor-" + index}, this.props) as BotMsaEditorProps);
                content = [msaEditor];
                break;
        }

        //only show errors on the currently selected version
        var endpointError = '';
        if (this.props.bot.Version === endpoint.Version) {
            endpointError = this.props.errors[reflectedName(x.Endpoint = null)];
        }
        var belowEndpointField = [endpointError ? <div key="version_error" className="error-info">{endpointError}</div> : null, <div key="version_tip" className="field-tip">HTTPS Rest endpoint that the bot implements to send and receive messages.</div>];

        return (
            <div className={'version-content ' + (BotVersionEditor.versionToClassname(endpoint.Version) + (enabled ? '' : ' disabled'))} disabled={!enabled}>
                <div className="detail-row">
                    <BlockRowInputLabel text={resourceStrings.botEndpoint} />
                    <input
                        disabled={!enabled}
                        type="text"
                        id={BotVersionEditor.endpointId(index)}
                        className={endpointError ? errorClass : ''}
                        placeholder="https URL"
                        value={endpoint.Url}
                        maxLength={1024}
                        onChange={ev => this.props.handleArrayIndexedObjectChange('Endpoints', index, 'Url', ev)}
                    />
                    {belowEndpointField}
                </div>
                {this.isStreamingSupported() ? 
                <div className="detail-row streaming-enable-row">
                    <div className="checkbox-rect">
                        <input 
                            id="is-streaming-supported"
                            type="checkbox"
                            defaultChecked={this.props.bot.IsStreamingSupported}
                            onChange={(ev) => this.props.handleCheckboxChange('IsStreamingSupported', ev)}
                        />
                    </div>
                    <BlockRowInputLabel for="is-streaming-supported" text={resourceStrings.botEnableStreamingEndpoint} />
                </div> : null }
                {content}
                <div className="detail-row">
                    <BlockRowInputLabel text={resourceStrings.botSchemaTransformationVersion} />
                    <div className="schema-transformation-version-row">
                        <select
                            id="schema-transformation-version-selector"
                            ref="schema-transformation-version-selector"
                            onChange={ev => this.props.handleChange('SchemaTransformationVersion', ev)}>
                            {
                                this.state.SchemaTransformationVersionList.map((option, i) => (<option value={option.val as any} key={`option_key_${i}`} selected={option.selected}>{option.name}</option>))
                            }
                        </select>
                    </div>
                </div>
            </div>
        );
    }

    private getSTVDisplayString(version: Rest.SchemaTransformationVersion): string {
        return "V" + version.Major + '.' + version.Minor;
    }

    private loadChannelSchemaTransformationInfo(): void {
        Rest.BotManagerClient.getAvailableSchemaTransformationVersionList().then(list => {
            var options = _.map(list, (version) => { return { name: this.getSTVDisplayString(version), val: JSON.stringify(version), selected: false }; });

            if (this.props.bot.SchemaTransformationVersion == null && options.length > 0) {
                // Set the min version as selected when stv version = null
                options[0].selected = true;
            } else {
                var botVersionJson = JSON.stringify(this.props.bot.SchemaTransformationVersion);
                options.forEach(item => {
                    if (item.val === botVersionJson) {
                        item.selected = true;
                    }
                });
            }

            this.setState({
                SchemaTransformationVersionList: options
            });
        });
    }

    private isStreamingSupported() {
        if(clientAppSettings.flightedFeatures.some(f => f == "speechchannel")){
            return true;
        }
        return false;
    }

    public render() {

        if (this.props.bot.Endpoints.length > 1) {

            var radios = this.props.bot.Endpoints.filter(endpt => endpt.Version !== RollbackEndpointVersionName)
                .map((endpoint, index) => {
                    return this.versionRadio(index);
            });

            return (
                <div>
                    <div className="detail-row version-group-label">
                        <label id="version" className="label-above">{resourceStrings.botFramework} version</label>
                        <div className="field-tip">The version of the {resourceStrings.botFramework} rest API that your bot supports.</div>
                    </div>
                    <div>
                        {radios}
                    </div>
                </div>
            );

        } else {
            return <div>{this.versionContent(0, true)}</div>;
        }

    }
}



// WEBPACK FOOTER //
// ./Client/Views/BotVersionEditor.tsx